"use client";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { useRender } from "@base-ui/react/use-render";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../lib/utils";
import { AnimatedLoadingSpinner } from "../animated-loading-spinner";

function ButtonRenderSlot({
  render,
  ref,
  ...props
}: {
  render: React.ReactElement;
  ref?: React.Ref<HTMLElement>;
} & React.HTMLAttributes<HTMLElement>) {
  return useRender({ render, ref, props });
}

export type DocumentedButtonProps = VariantProps<typeof buttonVariants> & {
  /**
   * Display a loading spinner instead of the children
   */
  loading?: boolean;

  /**
   * Disables the button
   */
  disabled?: boolean;

  /**
   * Keyboard shortcut to trigger the `onClick` handler
   */
  keyboard?: {
    /**
     * The shortcut displayed on the button
     */
    display: string;
    /**
     * Decide whether the button should be pressed
     * Return true to trigger the callback function.
     * @example: (e)=> e.key === "a"
     */
    trigger: (e: KeyboardEvent) => boolean;
    /**
     * The function to be called
     */
    callback: (e: KeyboardEvent) => void | Promise<void>;
  };

  /**
   * Render your own element/component as the button (replaces the former `asChild`)
   */
  render?: React.ComponentProps<typeof ButtonPrimitive>["render"];

  /**
   * Optional label for screen readers when in loading state
   */
  loadingLabel?: string;
};

const buttonVariants = cva(
  [
    "inline-flex group relative isolate items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium select-none cursor-pointer",
    "transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out",
    "motion-safe:active:not-disabled:not-aria-disabled:translate-y-px",
    "outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--focus-ring)",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-colors",
    "disabled:cursor-not-allowed aria-disabled:cursor-not-allowed",
  ],
  {
    variants: {
      variant: {
        default: "",
        destructive: "",
        primary: [
          "px-2.5 py-2 border border-transparent",
          "disabled:bg-grayA-4 disabled:bg-none disabled:text-gray-9 disabled:shadow-none disabled:border-transparent",
          "disabled:[&_svg]:text-gray-8",
        ],
        outline: [
          "px-2.5 py-2 text-gray-12 bg-raised border border-grayA-5 shadow-xs",
          "hover:bg-table-header hover:border-grayA-7 aria-expanded:bg-table-header aria-expanded:border-grayA-7 active:bg-gray-3",
          "[&_svg]:text-gray-10 hover:[&_svg]:text-gray-11",
          "disabled:bg-transparent disabled:text-gray-8 disabled:border-grayA-4 disabled:shadow-none disabled:[&_svg]:text-gray-7",
        ],
        ghost: [
          "px-2.5 py-2 text-gray-11 bg-transparent border border-transparent",
          "hover:bg-grayA-3 hover:text-gray-12 aria-expanded:bg-grayA-3 aria-expanded:text-gray-12 active:bg-grayA-4",
          "[&_svg]:text-gray-10 hover:[&_svg]:text-gray-12",
          "disabled:text-gray-8 disabled:bg-transparent disabled:[&_svg]:text-gray-7",
        ],
      },
      shape: {
        square: "size-8 p-1",
      },
      color: {
        default: "",
        success: "",
        warning: "",
        danger: "",
        info: "",
      },
      size: {
        icon: "size-6 p-1",
        sm: "h-7",
        md: "h-8",
        lg: "h-9 px-3",
        xlg: "h-10 px-3.5 text-base",
        "2xlg": "h-12 px-4 text-base rounded-lg",
      },
    },
    defaultVariants: {
      variant: "primary",
      color: "default",
      size: "sm",
    },
    compoundVariants: [
      {
        variant: "primary",
        color: "default",
        className: [
          // The site's primary: a flat ink slab (white in dark mode).
          "text-gray-1 bg-gray-12 shadow-xs",
          "hover:bg-[color-mix(in_oklab,var(--color-gray-12)_86%,var(--color-gray-1))] aria-expanded:bg-[color-mix(in_oklab,var(--color-gray-12)_86%,var(--color-gray-1))]",
          "[&_svg]:text-gray-1/70 hover:[&_svg]:text-gray-1",
        ],
      },
      {
        variant: "primary",
        color: "danger",
        className: [
          "text-white bg-error-9 bg-linear-to-b from-white/12 to-white/0 border-errorA-11/30",
          "shadow-[inset_0_1px_0_rgb(255_255_255/0.2),0_1px_2px_var(--color-errorA-6)]",
          "hover:bg-error-10 active:bg-error-10 [&_svg]:text-white/80",
        ],
      },
      {
        variant: "outline",
        color: "danger",
        className:
          "text-error-11 border-errorA-6 hover:bg-error-2 hover:border-errorA-8 active:bg-error-3 dark:hover:bg-errorA-3 [&_svg]:text-error-10 hover:[&_svg]:text-error-11 disabled:text-errorA-7 disabled:border-errorA-4",
      },
      {
        variant: "ghost",
        color: "danger",
        className:
          "text-error-11 hover:bg-errorA-3 hover:text-error-11 active:bg-errorA-4 [&_svg]:text-error-10 hover:[&_svg]:text-error-11 disabled:text-errorA-7",
      },
      {
        variant: "primary",
        color: "warning",
        className: [
          "text-warning-12 bg-warning-9 bg-linear-to-b from-white/20 to-white/0 border-warningA-11/25",
          "shadow-[inset_0_1px_0_rgb(255_255_255/0.3),0_1px_2px_var(--color-warningA-7)]",
          "hover:bg-warning-10 active:bg-warning-10 dark:text-warning-1 [&_svg]:text-warning-12/70 dark:[&_svg]:text-warning-1/70",
        ],
      },
      {
        variant: "outline",
        color: "warning",
        className:
          "text-warning-11 border-warningA-7 hover:bg-warning-2 hover:border-warningA-8 active:bg-warning-3 dark:hover:bg-warningA-3 [&_svg]:text-warning-11 disabled:text-warningA-7",
      },
      {
        variant: "ghost",
        color: "warning",
        className:
          "text-warning-11 hover:bg-warningA-3 hover:text-warning-11 active:bg-warningA-4 [&_svg]:text-warning-11 disabled:text-warningA-7",
      },
      {
        variant: "primary",
        color: "success",
        className: [
          "text-white bg-success-9 bg-linear-to-b from-white/12 to-white/0 border-successA-11/30",
          "shadow-[inset_0_1px_0_rgb(255_255_255/0.2),0_1px_2px_var(--color-successA-6)]",
          "hover:bg-success-10 active:bg-success-10 dark:text-success-1 [&_svg]:text-white/80 dark:[&_svg]:text-success-1/70",
        ],
      },
      {
        variant: "outline",
        color: "success",
        className:
          "text-success-11 border-successA-6 hover:bg-success-2 hover:border-successA-8 active:bg-success-3 dark:hover:bg-successA-3 [&_svg]:text-success-10 disabled:text-successA-7",
      },
      {
        variant: "ghost",
        color: "success",
        className:
          "text-success-11 hover:bg-successA-3 hover:text-success-11 active:bg-successA-4 [&_svg]:text-success-10 disabled:text-successA-7",
      },
      {
        variant: "primary",
        color: "info",
        className: [
          "text-white bg-info-9 bg-linear-to-b from-white/12 to-white/0 border-infoA-11/30",
          "shadow-[inset_0_1px_0_rgb(255_255_255/0.2),0_1px_2px_var(--color-infoA-6)]",
          "hover:bg-info-10 active:bg-info-10 [&_svg]:text-white/80",
        ],
      },
      {
        variant: "outline",
        color: "info",
        className:
          "text-info-11 border-infoA-6 hover:bg-info-2 hover:border-infoA-8 active:bg-info-3 dark:hover:bg-infoA-3 [&_svg]:text-info-10 disabled:text-infoA-7",
      },
      {
        variant: "ghost",
        color: "info",
        className:
          "text-info-11 hover:bg-infoA-3 hover:text-info-11 active:bg-infoA-4 [&_svg]:text-info-10 disabled:text-infoA-7",
      },
    ],
  },
);

export type ButtonProps = VariantProps<typeof buttonVariants> &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    /**
     * Display a loading spinner instead of the children
     */
    loading?: boolean;
    disabled?: boolean;
    /**
     * Keyboard shortcut to trigger the `onClick` handler
     */
    keyboard?: {
      /**
       * The shortcut displayed on the button
       */
      display: string;
      /**
       * Decide whether the button should be pressed
       * Return true to trigger the callback function.
       * @example: (e)=> e.key === "a"
       */
      trigger: (e: KeyboardEvent) => boolean;
      /**
       * The function to be called
       */
      callback: (e: KeyboardEvent) => void | Promise<void>;
    };
    /**
     * Render your own element/component as the button (replaces the former `asChild`)
     */
    render?: React.ComponentProps<typeof ButtonPrimitive>["render"];
    /**
     * Optional label for screen readers when in loading state
     */
    loadingLabel?: string;
    ref?: React.Ref<HTMLButtonElement>;
  };

const keyboardIconVariants = cva(
  "inline-flex items-center justify-center font-mono text-[10px] leading-none font-medium rounded-[5px] h-[18px] min-w-[18px] px-1 transition-colors duration-150 -mr-1",
  {
    variants: {
      variant: {
        default: "bg-grayA-3 text-gray-11 shadow-[inset_0_-1px_0_var(--color-grayA-5)]",
        primary: "bg-gray-1/15 text-gray-1/90 shadow-[inset_0_-1px_0_rgb(0_0_0/0.15)]",
        outline: "bg-grayA-3 text-gray-11 shadow-[inset_0_-1px_0_var(--color-grayA-5)]",
        ghost: "bg-grayA-3 text-gray-11 shadow-[inset_0_-1px_0_var(--color-grayA-5)]",
        danger: "bg-errorA-3 text-error-11 shadow-[inset_0_-1px_0_var(--color-errorA-5)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;

type ButtonColor = NonNullable<VariantProps<typeof buttonVariants>["color"]>;

const VARIANT_MAP: Record<string, { variant: ButtonVariant; color?: ButtonColor }> = {
  default: { variant: "primary" },
  destructive: { variant: "primary", color: "danger" },
};

function Button({
  className,
  variant,
  color = "default",
  size,
  render,
  loading,
  disabled,
  loadingLabel = "Loading, please wait",
  ref,
  ...props
}: ButtonProps) {
  let mappedVariant: ButtonVariant = "primary";
  let mappedColor: ButtonColor = color;
  if (variant === null || variant === undefined) {
    mappedVariant = "primary";
  } else if (VARIANT_MAP[variant as keyof typeof VARIANT_MAP]) {
    const mapping = VARIANT_MAP[variant as keyof typeof VARIANT_MAP];
    mappedVariant = mapping.variant;
    if (mapping.color) {
      mappedColor = mapping.color;
    }
  } else {
    mappedVariant = variant as ButtonVariant;
  }
  const isClickDisabled = disabled || loading;
  const isVisuallyDisabled = disabled;
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [buttonWidth, setButtonWidth] = React.useState<number | undefined>(undefined);
  React.useEffect(() => {
    if (loading && buttonRef.current && !buttonWidth) {
      setButtonWidth(buttonRef.current.offsetWidth);
    } else if (!loading) {
      setButtonWidth(undefined);
    }
  }, [loading, buttonWidth]);
  React.useEffect(() => {
    if (!props.keyboard || isClickDisabled) {
      return;
    }
    const down = (e: KeyboardEvent) => {
      if (!props.keyboard?.trigger(e)) {
        return;
      }
      e.preventDefault();
      props.keyboard?.callback(e);
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [props.keyboard, isClickDisabled]);

  // A `render` target is usually a link; the Base UI Button primitive would
  // bolt role="button" and key handlers onto it. The spinner and keyboard hint
  // are intentionally omitted on this path.
  if (render) {
    return (
      <ButtonRenderSlot
        render={render as React.ReactElement}
        className={cn(
          buttonVariants({
            variant: mappedVariant,
            color: mappedColor,
            size,
            className,
          }),
        )}
        onClick={loading ? undefined : props.onClick}
        aria-disabled={isClickDisabled || undefined}
        aria-busy={loading || undefined}
        ref={ref as React.Ref<HTMLElement>}
        {...(props as React.HTMLAttributes<HTMLElement>)}
      />
    );
  }

  return (
    <ButtonPrimitive
      className={cn(
        buttonVariants({
          variant: mappedVariant,
          color: mappedColor,
          size,
          className,
        }),
      )}
      onClick={loading ? undefined : props.onClick}
      disabled={isVisuallyDisabled}
      aria-disabled={isClickDisabled}
      aria-busy={loading}
      ref={ref}
      {...props}
    >
      {loading && (
        <div
          className="absolute inset-0 flex items-center justify-center w-full h-full transition-opacity duration-200"
          aria-hidden="true"
        >
          <AnimatedLoadingSpinner />
          <span className="sr-only">{loadingLabel}</span>
        </div>
      )}
      <div
        className={cn(
          "w-full h-full flex items-center justify-center gap-1.5 transition-opacity duration-200",
          {
            "opacity-0": loading,
            "opacity-100": !loading,
          },
        )}
      >
        {props.children}
        {props.keyboard ? (
          <kbd
            className={cn(
              keyboardIconVariants({
                variant:
                  mappedVariant === "primary" && mappedColor !== "warning"
                    ? "primary"
                    : mappedColor === "danger"
                      ? "danger"
                      : "default",
              }),
            )}
          >
            {props.keyboard.display}
          </kbd>
        ) : null}{" "}
      </div>
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
